import { useCallback, useMemo } from "react";
import { useAuth } from "@/auth/AuthContext";
import { apiUrl } from "@/config";
import { claimReauthAttempt, clearReauthAttempt } from "./reauth";

interface ApiErrorBody {
  error?: string;
}

/**
 * Only reached when a redirect to sign in is under way, so this resolves late
 * purely as a safety net: if the navigation never happens, the caller gets a
 * normal failure instead of a spinner that never ends.
 */
const REDIRECT_SAFETY_MS = 15_000;

/**
 * Resets on page load, unlike the session-scoped claim. Concurrent requests
 * that 401 together must all wait for the one redirect rather than the losers
 * reporting a failure the page is about to navigate away from.
 */
let redirectInFlight = false;

function navigatingAway(): Promise<null> {
  return new Promise((resolve) =>
    setTimeout(() => resolve(null), REDIRECT_SAFETY_MS),
  );
}

export function useApi() {
  const { accessToken, login } = useAuth();

  const get = useCallback(
    async <T>(path: string): Promise<T | null> => {
      try {
        const response = await fetch(`${apiUrl}${path}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.status === 401) {
          if (redirectInFlight) {
            return navigatingAway();
          }

          if (!claimReauthAttempt()) {
            return null;
          }

          redirectInFlight = true;

          void login();

          return navigatingAway();
        }

        if (!response.ok) {
          return null;
        }

        clearReauthAttempt();

        return (await response.json()) as T;
      } catch {
        // The browser aborts in-flight requests when it leaves the page.
        if (redirectInFlight) {
          return navigatingAway();
        }

        return null;
      }
    },
    [accessToken, login],
  );

  const getText = useCallback(
    async (path: string): Promise<string | null> => {
      try {
        const response = await fetch(`${apiUrl}${path}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.status === 401) {
          if (redirectInFlight) {
            return navigatingAway();
          }

          if (!claimReauthAttempt()) {
            return null;
          }

          redirectInFlight = true;

          void login();

          return navigatingAway();
        }

        if (!response.ok) {
          return null;
        }

        clearReauthAttempt();

        return await response.text();
      } catch {
        if (redirectInFlight) {
          return navigatingAway();
        }

        return null;
      }
    },
    [accessToken, login],
  );

  const getBinary = useCallback(
    async (path: string): Promise<ArrayBuffer | null> => {
      try {
        const response = await fetch(`${apiUrl}${path}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (response.status === 401) {
          if (redirectInFlight) {
            return navigatingAway();
          }

          if (!claimReauthAttempt()) {
            return null;
          }

          redirectInFlight = true;

          void login();

          return navigatingAway();
        }

        if (!response.ok) {
          return null;
        }

        clearReauthAttempt();

        return await response.arrayBuffer();
      } catch {
        if (redirectInFlight) {
          return navigatingAway();
        }

        return null;
      }
    },
    [accessToken, login],
  );

  const send = useCallback(
    async <T = void>(
      path: string,
      method: "POST" | "PUT" | "PATCH" | "DELETE",
      body?: unknown,
      fallbackMessage = "Request failed.",
    ): Promise<T> => {
      const isBlob = body instanceof Blob;
      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
      };

      if (body !== undefined) {
        headers["Content-Type"] = isBlob ? body.type : "application/json";
      }

      const response = await fetch(`${apiUrl}${path}`, {
        method,
        headers,
        body: isBlob || body === undefined ? body : JSON.stringify(body),
      });

      if (response.status === 401) {
        if (!redirectInFlight && !claimReauthAttempt()) {
          throw new Error("Your session has expired. Reload to sign in again.");
        }

        if (!redirectInFlight) {
          redirectInFlight = true;

          void login();
        }

        throw new Error("Your session has expired. Signing you in again.");
      }

      if (!response.ok) {
        const errorBody = (await response
          .json()
          .catch(() => null)) as ApiErrorBody | null;

        throw new Error(errorBody?.error ?? fallbackMessage);
      }

      clearReauthAttempt();

      const text = await response.text();

      return (text ? JSON.parse(text) : undefined) as T;
    },
    [accessToken, login],
  );

  return useMemo(
    () => ({ get, getText, getBinary, send }),
    [get, getText, getBinary, send],
  );
}
